import axios, { type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "../store/auth";

const baseURL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

// Endpoints that must never carry a Bearer token. /auth/login/ trades
// credentials for a fresh token pair; /auth/refresh/ swaps a refresh
// token for a new access token. Attaching a stale access token to
// either is actively harmful: DRF's JWTAuthentication runs *before*
// the view's [AllowAny] permission, so an expired/blacklisted token
// is rejected with 401 "Given token not valid for any token type"
// and the request never reaches the login serializer — locking out
// a user who simply has stale tokens in their browser.
const PUBLIC_AUTH_PATHS = ["/auth/login", "/auth/refresh"] as const;

function isPublicAuthPath(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_AUTH_PATHS.some((p) => url.includes(p));
}

export const api = axios.create({
  baseURL,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  if (isPublicAuthPath(config.url)) return config;
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  const { refreshToken: refresh } = useAuthStore.getState();
  if (!refresh) return null;
  if (!refreshing) {
    refreshing = axios
      .post(`${baseURL}/auth/refresh/`, { refresh })
      .then((res) => {
        const access = res.data.access as string;
        useAuthStore.getState().setTokens({
          access,
          refresh: res.data.refresh || refresh,
        });
        return access;
      })
      .catch(() => {
        useAuthStore.getState().logout();
        return null;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as RetryableConfig | undefined;
    if (
      original &&
      error.response?.status === 401 &&
      !original._retry &&
      !isPublicAuthPath(original.url)
    ) {
      original._retry = true;
      const token = await refreshToken();
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
    }
    return Promise.reject(error);
  }
);
